import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * `/redefinir-password?token=...` — set a new password using the token from email.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center" data-testid="reset-no-token">
          <p className="text-[10px] uppercase tracking-widest text-red-500 mb-3 font-bold">Link inválido</p>
          <h1 className="font-display text-3xl text-[#0a0f1e]">Este link não é válido</h1>
          <p className="text-slate-500 mt-2 text-sm">
            O endereço que abriu está incompleto ou incorrecto. Peça um novo email de recuperação.
          </p>
          <Link
            to="/esqueci-password"
            className="mt-6 inline-block h-12 leading-[3rem] px-6 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase tracking-widest text-sm"
            data-testid="reset-new-request"
          >
            Pedir novo link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) {
      setErr("A palavra-passe deve ter pelo menos 8 caracteres.");
      return;
    }
    if (pw !== pw2) {
      setErr("As palavras-passe não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Palavra-passe atualizada!");
      setTimeout(() => navigate("/login"), 2500);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#f59e0b] uppercase tracking-widest font-semibold mb-8"
          data-testid="reset-back-to-login"
        >
          <ArrowLeft className="w-3 h-3" /> Voltar ao login
        </Link>

        <p className="text-[10px] uppercase tracking-widest text-[#f59e0b] mb-3 font-bold">Nova palavra-passe</p>
        <h1 className="font-display text-4xl text-[#0a0f1e]">Escolha uma nova palavra-passe</h1>
        <p className="text-slate-500 mt-2 text-sm">
          Mínimo 8 caracteres. Guarde-a num sítio seguro.
        </p>

        {done ? (
          <div className="mt-8 border border-emerald-200 bg-emerald-50 p-5 text-sm" data-testid="reset-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Palavra-passe alterada com sucesso</div>
                <p className="mt-1 text-emerald-800">A redireccionar para o login…</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <Label className="label-caps mb-2 block" htmlFor="rp-pw">Nova palavra-passe</Label>
              <div className="relative">
                <Input
                  id="rp-pw"
                  data-testid="reset-password"
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="h-12 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b] text-[#0a0f1e] pr-11"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  data-testid="reset-password-toggle"
                  aria-label={show ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-500 hover:text-[#f59e0b]"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="label-caps mb-2 block" htmlFor="rp-pw2">Confirmar palavra-passe</Label>
              <Input
                id="rp-pw2"
                data-testid="reset-password-confirm"
                type={show ? "text" : "password"}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="h-12 bg-transparent border-slate-200 rounded-none focus:border-[#f59e0b] text-[#0a0f1e]"
                minLength={8}
                required
              />
            </div>
            {err && (
              <div data-testid="reset-error" className="text-sm text-red-500 border-l-2 border-red-500 pl-3">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full h-12 rounded-none bg-[#f59e0b] hover:bg-[#d97706] text-[#0a0f1e] font-semibold tracking-wide"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redefinir palavra-passe"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
